/**
 * Comprehensive screen reader announcement system
 * Provides queued announcements, priority handling, and message management
 */

export type AnnouncementPriority = 'polite' | 'assertive';
export type AnnouncementType = 'info' | 'success' | 'warning' | 'error' | 'status' | 'progress';

export interface AnnouncementMessage {
  id: string;
  message: string;
  priority: AnnouncementPriority;
  type: AnnouncementType;
  timestamp: number;
  timeout?: number;
  persistent?: boolean;
}

export interface AnnouncementOptions {
  priority?: AnnouncementPriority;
  type?: AnnouncementType;
  timeout?: number;
  persistent?: boolean;
  interrupt?: boolean;
  deduplicate?: boolean;
}

export interface AnnouncementQueue {
  polite: AnnouncementMessage[];
  assertive: AnnouncementMessage[];
}

export class ScreenReaderAnnouncer {
  private queue: AnnouncementQueue = {
    polite: [],
    assertive: []
  };
  
  private currentAnnouncement: AnnouncementMessage | null = null;
  private liveRegions: Map<AnnouncementPriority, HTMLElement> = new Map();
  private messageHistory: AnnouncementMessage[] = [];
  private isProcessing = false;
  private processingTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.createLiveRegions();
  }

  /**
   * Create ARIA live regions for announcements
   */
  private createLiveRegions(): void {
    // Create polite live region
    const politeRegion = document.createElement('div');
    politeRegion.setAttribute('aria-live', 'polite');
    politeRegion.setAttribute('aria-atomic', 'true');
    politeRegion.setAttribute('aria-relevant', 'additions text');
    politeRegion.className = 'sr-only';
    politeRegion.id = 'sr-announcer-polite';
    document.body.appendChild(politeRegion);
    this.liveRegions.set('polite', politeRegion);

    // Create assertive live region
    const assertiveRegion = document.createElement('div');
    assertiveRegion.setAttribute('aria-live', 'assertive');
    assertiveRegion.setAttribute('aria-atomic', 'true');
    assertiveRegion.setAttribute('aria-relevant', 'additions text');
    assertiveRegion.className = 'sr-only';
    assertiveRegion.id = 'sr-announcer-assertive';
    document.body.appendChild(assertiveRegion);
    this.liveRegions.set('assertive', assertiveRegion);
  }

  /**
   * Announce a message to screen readers
   */
  announce(message: string, options: AnnouncementOptions = {}): string {
    const {
      priority = 'polite',
      type = 'info',
      timeout = 5000,
      persistent = false,
      interrupt = false,
      deduplicate = true
    } = options;

    // Generate unique ID
    const id = `announcement-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Check for duplicate messages if deduplication is enabled
    if (deduplicate && this.isDuplicate(message, priority)) {
      return id;
    }

    const announcement: AnnouncementMessage = {
      id,
      message: this.formatMessage(message, type),
      priority,
      type,
      timestamp: Date.now(),
      timeout: persistent ? undefined : timeout,
      persistent
    };

    // Handle interruption
    if (interrupt && priority === 'assertive') {
      this.clearQueue('assertive');
      this.clearCurrentAnnouncement();
    }

    // Add to appropriate queue
    this.queue[priority].push(announcement);
    this.messageHistory.push(announcement);

    // Start processing if not already processing
    if (!this.isProcessing) {
      this.processQueue();
    }

    return id;
  }

  /**
   * Announce success message
   */
  announceSuccess(message: string, options: Omit<AnnouncementOptions, 'type'> = {}): string {
    return this.announce(message, { ...options, type: 'success' });
  }

  /**
   * Announce error message
   */
  announceError(message: string, options: Omit<AnnouncementOptions, 'type'> = {}): string {
    return this.announce(message, { 
      ...options, 
      type: 'error', 
      priority: 'assertive',
      interrupt: true 
    });
  }

  /**
   * Announce warning message
   */
  announceWarning(message: string, options: Omit<AnnouncementOptions, 'type'> = {}): string {
    return this.announce(message, { ...options, type: 'warning' });
  }

  /**
   * Announce status update
   */
  announceStatus(message: string, options: Omit<AnnouncementOptions, 'type'> = {}): string {
    return this.announce(message, { ...options, type: 'status' });
  }

  /**
   * Announce progress update
   */
  announceProgress(message: string, options: Omit<AnnouncementOptions, 'type'> = {}): string {
    return this.announce(message, { ...options, type: 'progress', deduplicate: false });
  }

  /**
   * Cancel a specific announcement
   */
  cancel(id: string): boolean {
    // Remove from queues
    let found = false;
    
    this.queue.polite = this.queue.polite.filter(msg => {
      if (msg.id === id) {
        found = true;
        return false;
      }
      return true;
    });

    this.queue.assertive = this.queue.assertive.filter(msg => {
      if (msg.id === id) {
        found = true;
        return false;
      }
      return true;
    });

    // Clear current announcement if it matches
    if (this.currentAnnouncement?.id === id) {
      this.clearCurrentAnnouncement();
      found = true;
    }

    return found;
  }

  /**
   * Clear all announcements
   */
  clear(): void {
    this.clearQueue('polite');
    this.clearQueue('assertive');
    this.clearCurrentAnnouncement();
  }

  /**
   * Clear queue for specific priority
   */
  clearQueue(priority: AnnouncementPriority): void {
    this.queue[priority] = [];
  }

  /**
   * Get announcement history
   */
  getHistory(limit?: number): AnnouncementMessage[] {
    const history = [...this.messageHistory].reverse();
    return limit ? history.slice(0, limit) : history;
  }

  /**
   * Get current queue status
   */
  getQueueStatus(): { polite: number; assertive: number; processing: boolean } {
    return {
      polite: this.queue.polite.length,
      assertive: this.queue.assertive.length,
      processing: this.isProcessing
    };
  }

  /**
   * Process the announcement queue
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    
    this.isProcessing = true;

    while (this.hasQueuedAnnouncements()) {
      // Prioritize assertive announcements
      const announcement = this.getNextAnnouncement();
      
      if (announcement) {
        await this.playAnnouncement(announcement);
      }
    }

    this.isProcessing = false;
  }

  /**
   * Check if there are queued announcements
   */
  private hasQueuedAnnouncements(): boolean {
    return this.queue.assertive.length > 0 || this.queue.polite.length > 0;
  }

  /**
   * Get the next announcement to play
   */
  private getNextAnnouncement(): AnnouncementMessage | null {
    // Prioritize assertive announcements
    if (this.queue.assertive.length > 0) {
      return this.queue.assertive.shift() || null;
    }
    
    if (this.queue.polite.length > 0) {
      return this.queue.polite.shift() || null;
    }

    return null;
  }

  /**
   * Play an announcement
   */
  private async playAnnouncement(announcement: AnnouncementMessage): Promise<void> {
    this.currentAnnouncement = announcement;
    
    const liveRegion = this.liveRegions.get(announcement.priority);
    if (!liveRegion) return;

    // Clear the region first to ensure the message is announced
    liveRegion.textContent = '';
    
    // Small delay to ensure screen readers pick up the change
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Set the announcement text
    liveRegion.textContent = announcement.message;

    // Set up timeout to clear the message
    if (announcement.timeout) {
      this.processingTimeout = setTimeout(() => {
        if (liveRegion.textContent === announcement.message) {
          liveRegion.textContent = '';
        }
        this.currentAnnouncement = null;
      }, announcement.timeout);
    }

    // Wait for the announcement to be processed
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Clear current announcement
   */
  private clearCurrentAnnouncement(): void {
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }

    // Clear live regions
    this.liveRegions.forEach(region => {
      region.textContent = '';
    });

    this.currentAnnouncement = null;
  }

  /**
   * Check if message is duplicate
   */
  private isDuplicate(message: string, priority: AnnouncementPriority): boolean {
    const recentMessages = this.messageHistory
      .filter(msg => msg.priority === priority)
      .slice(-3); // Check last 3 messages

    return recentMessages.some(msg => 
      msg.message === this.formatMessage(message, 'info') && 
      Date.now() - msg.timestamp < 2000 // Within 2 seconds
    );
  }

  /**
   * Format message based on type
   */
  private formatMessage(message: string, type: AnnouncementType): string {
    const prefixes: Record<AnnouncementType, string> = {
      info: '',
      success: 'Success: ',
      warning: 'Warning: ',
      error: 'Error: ',
      status: 'Status: ',
      progress: 'Progress: '
    };

    const prefix = prefixes[type] || '';
    return `${prefix}${message}`;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.clear();
    
    // Remove live regions from DOM
    this.liveRegions.forEach(region => {
      if (document.body.contains(region)) {
        document.body.removeChild(region);
      }
    });
    
    this.liveRegions.clear();
    this.messageHistory = [];
  }
}

// Global instance
let globalAnnouncer: ScreenReaderAnnouncer | null = null;

/**
 * Get or create global announcer instance
 */
export const getGlobalAnnouncer = (): ScreenReaderAnnouncer => {
  if (!globalAnnouncer) {
    globalAnnouncer = new ScreenReaderAnnouncer();
  }
  return globalAnnouncer;
};

/**
 * Cleanup global announcer
 */
export const cleanupGlobalAnnouncer = (): void => {
  if (globalAnnouncer) {
    globalAnnouncer.destroy();
    globalAnnouncer = null;
  }
};